// Presentational estimate summary panel — all state via props so it renders
// identically in the live intake form (wrapped by EstimateSummaryPanel, which
// supplies redux state) and in the admin branding preview (static sample
// state). Shows area stats, selected product count, and the preliminary
// estimate range. aria-live region announces estimate changes to screen
// readers.
import {
  Box, Paper, Typography, Button, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { materialSymbolSx } from '../../theme.js';
import {
  CUSTOM_QUOTE_LABEL, isCustomQuote, isPartialCustomQuote, customQuoteDetail,
  PARTIAL_CUSTOM_QUOTE_NOTE, estimateRangeText,
} from '../../utils/estimate-format.js';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

const StatRow = ({ label, value }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1,
  }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

// Circular badge holding a Material Symbols glyph; colors are passed in so the
// same badge works for the success (green) and primary (navy) perk cards.
const PerkIcon = ({ icon, bg, color }) => (
  <Box sx={{
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    bgcolor: bg,
    color,
  }}
  >
    <Box component="span" aria-hidden="true" sx={{ ...materialSymbolSx, fontSize: 24 }}>
      {icon}
    </Box>
  </Box>
);

// Value-prop card shown beneath the estimate. `highlight` tints the surface in
// the success color (mockup's green "economies of scale" panel); otherwise it
// is a plain outlined surface (the "data ownership" panel). Both derive their
// colors from the theme so they read well in light and dark mode.
const PerkCard = ({
  icon, iconBg, iconColor, title, body, highlight,
}) => (
  <Paper
    variant="outlined"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.75,
      ...(highlight && {
        bgcolor: (theme) => alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
        borderColor: (theme) => alpha(theme.palette.success.main, 0.45),
      }),
    }}
  >
    <PerkIcon icon={icon} bg={iconBg} color={iconColor} />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, lineHeight: 1.3, color: highlight ? 'success.main' : 'text.primary' }}
      >
        {title}
      </Typography>
      {body && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {body}
        </Typography>
      )}
    </Box>
  </Paper>
);

const EstimateSummaryPanelView = ({
  area, estimate, selectedCount, onSendInquiry,
}) => {
  const hasArea = !!area;
  const disabled = !hasArea || selectedCount === 0;
  // The price is computed from the tile-snapped area when tile billing is on,
  // so the panel must show that same billable figure (not the raw boundary) to
  // stay consistent with the estimate below it.
  const tiled = hasArea && area.tileAreaSqMi != null;

  const renderEstimate = () => {
    if (disabled) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {hasArea ? 'Select at least one product.' : 'Choose a project area first.'}
        </Typography>
      );
    }
    if (estimate.loading) {
      return <Typography variant="body2" color="text.secondary">Calculating…</Typography>;
    }
    if (estimate.error) {
      return <Typography variant="body2" color="error">{estimate.error}</Typography>;
    }
    if (estimate.data) {
      if (isCustomQuote(estimate.data)) {
        return (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component="span"
                aria-hidden="true"
                sx={{ ...materialSymbolSx, fontSize: 22, color: 'primary.main' }}
              >
                request_quote
              </Box>
              <Typography variant="h6" component="p" color="primary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {CUSTOM_QUOTE_LABEL}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {customQuoteDetail(estimate.data)}
            </Typography>
          </>
        );
      }
      return (
        <>
          <Typography variant="h6" component="p" color="primary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {estimateRangeText(estimate.data)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Preliminary estimate — final pricing provided in formal proposal.
          </Typography>
          {isPartialCustomQuote(estimate.data) && (
            <Box sx={{
              display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5,
            }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  ...materialSymbolSx, fontSize: 16, color: 'text.secondary', mt: '1px',
                }}
              >
                info
              </Box>
              <Typography variant="caption" color="text.secondary">
                {PARTIAL_CUSTOM_QUOTE_NOTE}
              </Typography>
            </Box>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Paper
        component="aside"
        variant="outlined"
        aria-label="Estimate summary"
        sx={{
          display: 'flex', flexDirection: 'column', gap: 1.5, p: 2,
        }}
      >
        <Typography variant="overline" color="text.secondary">Your Estimate</Typography>

        {hasArea && (
          <>
            {tiled ? (
              <>
                <StatRow label="Selected area" value={`${fmt(area.areaSqMi)} sq mi`} />
                <StatRow label="Billable area" value={`${fmt(area.tileAreaSqMi)} sq mi`} />
                <StatRow label="Est. population" value={fmt(area.tileEstPopulation)} />
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Pricing is based on the whole map tiles your area covers.
                </Typography>
              </>
            ) : (
              <>
                <StatRow label="Area" value={`${fmt(area.areaSqMi)} sq mi`} />
                <StatRow label="Est. population" value={fmt(area.estPopulation)} />
              </>
            )}
            <StatRow label="Products selected" value={selectedCount} />
            <Divider />
          </>
        )}

        <Box sx={{ minHeight: 64 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Estimated investment
          </Typography>
          {/* aria-live so screen readers announce estimate updates */}
          <Box aria-live="polite" aria-atomic="true">
            {renderEstimate()}
          </Box>
        </Box>

        <Button
          variant="contained"
          fullWidth
          disabled={disabled}
          onClick={onSendInquiry}
          aria-disabled={disabled}
        >
          Send Inquiry
        </Button>
      </Paper>

      <PerkCard
        highlight
        icon="savings"
        iconBg="success.main"
        iconColor="success.contrastText"
        title="Leveraging statewide economies of scale."
      />
      <PerkCard
        icon="verified_user"
        iconBg="primary.main"
        iconColor="primary.contrastText"
        title="County Ownership of Data"
        body="Your county will own all data outright for internal use."
      />
    </Box>
  );
};

export default EstimateSummaryPanelView;
